// File: frontend/src/components/Leads/LeadsTable.jsx
// Leads table component

import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { getScoreColor, formatLeadScore, formatDate } from './leadFormat';
import LeadCard from './LeadCard';

// Menú de acciones por fila ("⋯"): Ver detalhes / Converter / Arquivar|Desarquivar.
const RowActionsMenu = ({ lead, onArchive, archivedView }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const isConverted = lead.lead_status === 'converted' || lead.lifecycle_stage === 'customer';

  return (
    <div className="relative inline-block text-left" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="p-1 rounded hover:bg-gray-100 text-gray-500"
        title="Ações"
      >
        <EllipsisVerticalIcon className="h-5 w-5" />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-44 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 py-1">
          <Link
            to={`/leads/${lead.id}`}
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Ver detalhes
          </Link>
          {!isConverted && !archivedView && (
            <Link
              to={`/leads/${lead.id}`}
              className="block px-4 py-2 text-sm text-green-700 hover:bg-gray-50"
            >
              Converter
            </Link>
          )}
          <button
            type="button"
            onClick={() => { setOpen(false); onArchive?.(lead); }}
            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            {archivedView ? 'Desarquivar' : 'Arquivar'}
          </button>
        </div>
      )}
    </div>
  );
};

// El bot SDR deja el producto que atendio en custom_fields.sdr_product. `lead_source` es
// first-touch y no se sobrescribe, asi que un lead que ya existia por otra via queda atribuido
// al origen viejo. Mostramos el ultimo toque al lado para que el aporte del bot sea visible.
function lastTouch(lead) {
  const p = lead?.custom_fields?.sdr_product;
  if (!p) return null;
  const src = String(lead.lead_source || '').toLowerCase();
  return src.includes(String(p).toLowerCase()) ? null : p;
}

function fonteTitle(lead) {
  const lt = lastTouch(lead);
  const src = lead?.lead_source || '-';
  return lt ? `${src} (origem) - ultimo toque: ${lt}` : src;
}

const LeadsTable = ({
  leads = [],
  pagination,
  onPageChange,
  onSort,
  currentSort,
  userRole,
  userPermissions,
  onArchive,
  archivedView = false
}) => {
  // The leads list API returns pagination in snake_case:
  // { total_items, current_page, per_page }. Normalize it (and derive total
  // pages) so the pagination controls render and can move past page 1.
  const page = pagination
    ? {
        currentPage: Number(pagination.current_page) || 1,
        perPage: Number(pagination.per_page) || 0,
        total: Number(pagination.total_items) || 0,
        totalPages:
          Number(pagination.per_page) > 0
            ? Math.ceil(Number(pagination.total_items || 0) / Number(pagination.per_page))
            : 0,
      }
    : null;

  return (
    <div>
      {/* Desktop: tabla (>= md) */}
      <div className="hidden md:block">
      <table className="w-full table-fixed divide-y divide-gray-200">
        <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
          <tr>
            <th className="w-[22%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome / Email</th>
            <th className="w-[18%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
            <th className="w-[8%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
            <th className="w-[14%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="w-[16%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fonte</th>
            <th className="w-[14%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Criado em</th>
            <th className="w-[8%] px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {leads.length === 0 ? (
            <tr>
              <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                <div className="flex flex-col items-center">
                  <svg className="w-12 h-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <p className="text-lg font-medium">Nenhum lead encontrado</p>
                  <p className="text-sm">Tente ajustar seus filtros ou criar um novo lead</p>
                </div>
              </td>
            </tr>
          ) : (
            leads.map((lead) => (
              <tr key={lead.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate" title={`${lead.first_name || ''} ${lead.last_name || ''}`}>
                      {lead.first_name} {lead.last_name}
                    </div>
                    <div className="text-sm text-gray-500 truncate" title={lead.email || ''}>{lead.email}</div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 truncate" title={lead.company_name || ''}>
                  {lead.company_name || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getScoreColor(lead.lead_score)}`}>
                    {formatLeadScore(lead.lead_score)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {lead.lead_status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 truncate" title={fonteTitle(lead)}>
                  <div className="truncate">{lead.lead_source || '-'}</div>
                  {lastTouch(lead) && (
                    <span className="mt-0.5 inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-600">
                      ult. {lastTouch(lead)}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(lead.created_at)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <RowActionsMenu lead={lead} onArchive={onArchive} archivedView={archivedView} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>

      {/* Mobile: tarjetas (< md) */}
      <div className="md:hidden divide-y divide-gray-200">
        {leads.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            <div className="flex flex-col items-center">
              <svg className="w-12 h-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-lg font-medium">Nenhum lead encontrado</p>
              <p className="text-sm">Tente ajustar seus filtros ou criar um novo lead</p>
            </div>
          </div>
        ) : (
          leads.map((lead) => (
            <LeadCard key={lead.id} lead={lead} onArchive={onArchive} archivedView={archivedView} />
          ))
        )}
      </div>

      {/* Pagination */}
      {page && page.totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => onPageChange(page.currentPage - 1)}
              disabled={page.currentPage <= 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <button
              onClick={() => onPageChange(page.currentPage + 1)}
              disabled={page.currentPage >= page.totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próximo
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Mostrando{' '}
                <span className="font-medium">
                  {(page.currentPage - 1) * page.perPage + 1}
                </span>{' '}
                até{' '}
                <span className="font-medium">
                  {Math.min(page.currentPage * page.perPage, page.total)}
                </span>{' '}
                de{' '}
                <span className="font-medium">{page.total}</span> resultados
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => onPageChange(page.currentPage - 1)}
                  disabled={page.currentPage <= 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Anterior</span>
                  ←
                </button>

                {[...Array(page.totalPages)].map((_, i) => {
                  const pageNum = i + 1;
                  if (
                    pageNum === 1 ||
                    pageNum === page.totalPages ||
                    (pageNum >= page.currentPage - 2 && pageNum <= page.currentPage + 2)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => onPageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNum === page.currentPage
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (
                    pageNum === page.currentPage - 3 ||
                    pageNum === page.currentPage + 3
                  ) {
                    return (
                      <span
                        key={pageNum}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                      >
                        ...
                      </span>
                    );
                  }
                  return null;
                })}

                <button
                  onClick={() => onPageChange(page.currentPage + 1)}
                  disabled={page.currentPage >= page.totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Próximo</span>
                  →
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsTable;
